import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import * as XLSX from 'xlsx'
import { useAuth } from '../context/AuthContext'
import {
  createCustomer,
  deleteCustomer,
  getCustomer,
  getCustomerTypes,
  getCustomersForExport,
  getCustomers,
  getRegions,
  getSalesExecutives,
  updateCustomer,
} from '../services/customer_service'
import './Customer.css'

const emptyForm = {
  name: '',
  contact_name: '',
  contact: '',
  region: '',
  region_id: '',
  sales_exec: '',
  sales_executive_id: '',
  address: '',
  type: '',
}
const getContactName = (customer) =>
  customer.cust_contact_name || customer.contactName || '-'
const getRegion = (customer) => customer.region || '-'
const getSalesExec = (customer) =>
  customer.sales_exec ||
  customer.sales_exec_name ||
  customer.salesExecutive ||
  customer.sales_executive ||
  '-'
const getCustomerType = (customer) =>
  customer.customertype ||
  customer.customer_type ||
  customer.cust_type ||
  customer.type ||
  '-'
const getOptionValue = (option) =>
  typeof option === 'object'
    ? option.id ||
      option.value ||
      option.regionid ||
      option.region_id ||
      option.sales_executive_id ||
      option.sales_exec_id ||
      option.customertypeid ||
      option.customer_type_id ||
      option.type_id ||
      option.name ||
      option.customertype ||
      option.label
    : option
const getOptionLabel = (option) =>
  typeof option === 'object'
    ? option.name ||
      option.label ||
      option.region ||
      option.region_name ||
      option.sales_exec_name ||
      option.sales_executive ||
      option.customertype ||
      option.value ||
      option.customertypeid ||
      option.id
    : option
const getMatchingOptionValue = (value, options) => {
  if (value && typeof value === 'object') return getOptionValue(value)
  const match = options.find(
    (option) =>
      normalizeType(getOptionValue(option)) === normalizeType(value) ||
      normalizeType(getOptionLabel(option)) === normalizeType(value),
  )
  return match ? getOptionValue(match) : (value ?? '')
}
const toCustomerForm = (customer, regions, salesExecutives) => ({
  ...emptyForm,
  id: customer.id ?? customer.customerid,
  name: customer.name ?? customer.cust_name ?? '',
  contact_name:
    customer.contact_name ??
    customer.cust_contact_name ??
    customer.contactName ??
    '',
  contact: customer.contact ?? customer.cust_contact ?? '',
  region_id: getMatchingOptionValue(
    customer.regionid ?? customer.region_id ?? customer.region,
    regions,
  ),
  sales_executive_id: getMatchingOptionValue(
    customer.sales_executive_id ??
      customer.sales_exec_id ??
      customer.sales_exec_name ??
      customer.salesExecutive ??
      customer.sales_executive,
    salesExecutives,
  ),
  address: customer.address ?? customer.cust_address ?? '',
  type:
    typeof (customer.cust_type ?? customer.customertype) === 'object'
      ? getOptionValue(customer.type ?? customer.customertype)
      : (customer.cust_type ??
        customer.customertype ??
        customer.customer_type ??
        customer.cust_type ??
        customer.customertypeid ??
        customer.customer_type_id ??
        customer.type_id ??
        ''),
})
const normalizeType = (value) =>
  String(value ?? '')
    .trim()
    .toLowerCase()
const getCustomerTypeValues = (customer) => {
  const values = [
    customer.customertype,
    customer.customer_type,
    customer.cust_type,
    customer.type,
    customer.customertypeid,
    customer.customer_type_id,
    customer.type_id,
  ]
  return values.flatMap((value) =>
    typeof value === 'object'
      ? [getOptionValue(value), getOptionLabel(value)]
      : [value],
  )
}

function CustomerModal({
  mode,
  customer,
  onClose,
  onSave,
  isSaving,
  regions,
  salesExecutives,
  customerTypes,
}) {
  const [form, setForm] = useState(
    customer ? { ...emptyForm, ...customer } : emptyForm,
  )
  const isDelete = mode === 'delete'
  const isEdit = mode === 'edit'
  const updateField = (event) =>
    setForm((current) => ({
      ...current,
      [event.target.name]: event.target.value,
    }))
  const submit = (event) => {
    event.preventDefault()
    if (isDelete) onSave(customer)
    else if (form.name.trim() && form.contact.trim() && form.address.trim())
      onSave(form)
  }

  return (
    <div
      className='customer-modal__backdrop'
      role='presentation'
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        className='customer-modal'
        role='dialog'
        aria-modal='true'
        aria-labelledby='customer-modal-title'
      >
        <div className='customer-modal__header'>
          <div>
            <span className='app-content__eyebrow'>
              {isDelete ? 'Confirm action' : 'Customer directory'}
            </span>
            <h2 id='customer-modal-title'>
              {isDelete
                ? 'Delete customer?'
                : isEdit
                  ? 'Edit customer'
                  : 'Add new customer'}
            </h2>
          </div>
          <button
            className='customer-modal__close'
            type='button'
            onClick={onClose}
            aria-label='Close modal'
          >
            x
          </button>
        </div>
        {isDelete ? (
          <>
            <p className='customer-modal__message'>
              This will permanently remove <strong>{customer.cust_name}</strong>{' '}
              from your customer list.
            </p>
            <div className='customer-modal__actions'>
              <button
                className='customer-modal__cancel'
                type='button'
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className='customer-modal__danger'
                type='button'
                onClick={() => onSave(customer)}
                disabled={isSaving}
              >
                {isSaving ? 'Deleting...' : 'Delete customer'}
              </button>
            </div>
          </>
        ) : (
          <form onSubmit={submit}>
            <div className='customer-form'>
              <label>
                Customer name
                <input
                  name='name'
                  value={form.name}
                  onChange={updateField}
                  required
                />
              </label>
              <label>
                Customer type
                <select
                  name='type'
                  value={form.type}
                  onChange={updateField}
                  required
                >
                  <option value=''>Select customer type</option>
                  {customerTypes.map((option) => (
                    <option
                      key={getOptionValue(option)}
                      value={getOptionValue(option)}
                    >
                      {getOptionLabel(option)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Contact
                <input
                  name='contact'
                  value={form.contact}
                  onChange={updateField}
                  required
                />
              </label>
              <label>
                Contact name
                <input
                  name='contact_name'
                  value={form.contact_name}
                  onChange={updateField}
                />
              </label>
              <label className='customer-form__wide'>
                Address
                <input
                  name='address'
                  value={form.address}
                  onChange={updateField}
                  required
                />
              </label>
              <label>
                Region
                <select
                  name='region_id'
                  value={form.region_id}
                  onChange={updateField}
                >
                  <option value=''>Select region</option>
                  {regions.map((option) => (
                    <option
                      key={getOptionValue(option)}
                      value={getOptionValue(option)}
                    >
                      {getOptionLabel(option)}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                Sales executive
                <select
                  name='sales_executive_id'
                  value={form.sales_executive_id}
                  onChange={updateField}
                >
                  <option value=''>Select sales exec</option>
                  {salesExecutives.map((option) => (
                    <option
                      key={getOptionValue(option)}
                      value={getOptionValue(option)}
                    >
                      {getOptionLabel(option)}
                    </option>
                  ))}
                </select>
              </label>
            </div>
            <div className='customer-modal__actions'>
              <button
                className='customer-modal__cancel'
                type='button'
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className='customer-modal__save'
                type='submit'
                disabled={isSaving}
              >
                {isSaving
                  ? 'Saving...'
                  : isEdit
                    ? 'Save changes'
                    : 'Add customer'}
              </button>
            </div>
          </form>
        )}
      </section>
    </div>
  )
}

function Customer() {
  const { user: authUser, hasPermission } = useAuth()
  const canAddCustomer = hasPermission('addCustomer')
  const canEditCustomer = hasPermission('editCustomer')
  const canDeleteCustomer = hasPermission('deleteCustomer')
  const [customers, setCustomers] = useState([])
  const [totalPerDay, setTotalPerDay] = useState(0)
  const [totalPerWeek, setTotalPerWeek] = useState(0)
  const [totalPerMonth, setTotalPerMonth] = useState(0)
  const [totalPerYear, setTotalPerYear] = useState(0)
  const [nextCursor, setNextCursor] = useState(null)
  const [nextPage, setNextPage] = useState(null)
  const [cursorHistory, setCursorHistory] = useState([])
  const [currentCursor, setCurrentCursor] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [search, setSearch] = useState('')
  const [typeFilter, setTypeFilter] = useState('All')
  const [modal, setModal] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState('')
  const [regions, setRegions] = useState([])
  const [salesExecutives, setSalesExecutives] = useState([])
  const [customerTypes, setCustomerTypes] = useState([])
  const [loadingCustomerId, setLoadingCustomerId] = useState(null)
  const [isExporting, setIsExporting] = useState(false)

  const loadCustomers = async (
    cursor = null,
    page = 1,
    searchTerm = search,
    signal,
  ) => {
    setIsLoading(true)
    setError('')
    try {
      const result = await getCustomers({
        cursor,
        page,
        search: searchTerm,
        signal,
      })
      if (signal?.aborted) return
      setCustomers(result.customers)
      setNextCursor(result.nextCursor)
      setNextPage(result.nextPage)
      setCurrentCursor(cursor)
      setCurrentPage(result.currentPage || page)
      setTotalPerDay(result.totalPerDay)
      setTotalPerWeek(result.totalPerWeek)
      setTotalPerMonth(result.totalPerMonth)
      setTotalPerYear(result.totalPerYear)
    } catch (requestError) {
      if (requestError.name !== 'AbortError') setError(requestError.message)
    } finally {
      if (!signal?.aborted) setIsLoading(false)
    }
  }

  useEffect(() => {
    Promise.all([getRegions(), getSalesExecutives(), getCustomerTypes()])
      .then(([loadedRegions, loadedSalesExecutives, loadedCustomerTypes]) => {
        setRegions(loadedRegions)
        setSalesExecutives(loadedSalesExecutives)
        setCustomerTypes(loadedCustomerTypes)
      })
      .catch((requestError) => setError(requestError.message))
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    const timer = window.setTimeout(
      () => {
        setCursorHistory([])
        loadCustomers(null, 1, search, controller.signal)
      },
      search ? 400 : 0,
    )

    return () => {
      window.clearTimeout(timer)
      controller.abort()
    }
  }, [search])

  const filteredCustomers = useMemo(
    () =>
      customers.filter((customer) => {
        const selectedType = customerTypes.find(
          (option) =>
            normalizeType(getOptionValue(option)) === normalizeType(typeFilter),
        )
        const selectedTypeValues = selectedType
          ? [getOptionValue(selectedType), getOptionLabel(selectedType)]
          : [typeFilter]
        const matchesType =
          typeFilter === 'All' ||
          selectedTypeValues.some((value) =>
            getCustomerTypeValues(customer).some(
              (customerValue) =>
                normalizeType(customerValue) === normalizeType(value),
            ),
          )
        return matchesType
      }),
    [customers, customerTypes, typeFilter],
  )
  const visibleCustomers = filteredCustomers

  const goToNextCursor = () => {
    if (!nextCursor && !nextPage) return
    setCursorHistory((history) => [
      ...history,
      { cursor: currentCursor, page: currentPage },
    ])
    loadCustomers(nextCursor, nextPage || currentPage)
  }

  const goToPreviousCursor = () => {
    if (!cursorHistory.length) return
    const history = [...cursorHistory]
    const previous = history.pop()
    setCursorHistory(history)
    loadCustomers(previous.cursor, previous.page)
  }

  const saveCustomer = async (value) => {
    setIsSaving(true)
    setError('')
    try {
      const customerPayload = {
        ...value,
        userid: authUser?.userid ?? authUser?.user_id ?? authUser?.id,
      }
      if (modal.mode === 'edit') await updateCustomer(value.id, customerPayload)
      else if (modal.mode === 'delete') await deleteCustomer(value.id)
      else await createCustomer(customerPayload)
      setCursorHistory([])
      await loadCustomers(null)
      setModal(null)
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsSaving(false)
    }
  }
  const openEditModal = async (customer) => {
    const customerId = customer.id ?? customer.customerid
    setLoadingCustomerId(customerId)
    setError('')
    try {
      const loadedCustomer = await getCustomer(customerId)
      setModal({
        mode: 'edit',
        customer: toCustomerForm(loadedCustomer, regions, salesExecutives),
      })
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setLoadingCustomerId(null)
    }
  }
  const exportCustomers = async () => {
    setIsExporting(true)
    setError('')
    try {
      const allCustomers = await getCustomersForExport()
      const worksheet = XLSX.utils.json_to_sheet(
        allCustomers.map((customer) => ({
          'Customer ID': customer.customerid ?? customer.id,
          Name: customer.cust_name ?? customer.name ?? '',
          'Contact name': getContactName(customer),
          Contact: customer.cust_contact ?? customer.contact ?? '',
          Address: customer.cust_address ?? customer.address ?? '',
          Region: getRegion(customer),
          'Sales executive': getSalesExec(customer),
          'Customer type': getCustomerType(customer),
          'Date added': customer.customer_date_added
            ? new Date(customer.customer_date_added).toLocaleDateString('en-GB')
            : '',
        })),
      )
      const workbook = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Customers')
      XLSX.writeFile(workbook, 'buck-press-all-customers.xlsx')
    } catch (requestError) {
      setError(requestError.message)
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <div className='customer-page'>
      <nav className='customer-page__breadcrumb' aria-label='Breadcrumb'>
        <Link to='/dashboard'>Dashboard</Link>
        <span aria-hidden='true'>/</span>
        <strong>Customers</strong>
      </nav>
      <header className='customer-page__header'>
        <div>
          <span className='app-content__eyebrow'>Directory</span>
          <h1>Customers</h1>
          <p>Manage your customer relationships and account details.</p>
        </div>
        {canAddCustomer && (
          <button
            className='customer-page__add-button'
            type='button'
            onClick={() => setModal({ mode: 'add' })}
          >
            <span aria-hidden='true'>+</span> Add new customer
          </button>
        )}
      </header>
      {error && (
        <div className='customer-page__error' role='alert'>
          {error}
          <button type='button' onClick={loadCustomers}>
            Retry
          </button>
        </div>
      )}
      <section className='customer-stats' aria-label='Customer totals'>
        <div className='customer-stat customer-stat--teal'>
          <span className='customer-stat__icon'>D</span>
          <span>
            <small>Added today</small>
            <strong>{totalPerDay}</strong>
          </span>
        </div>
        <div className='customer-stat customer-stat--orange'>
          <span className='customer-stat__icon'>W</span>
          <span>
            <small>Added this week</small>
            <strong>{totalPerWeek}</strong>
          </span>
        </div>
        <div className='customer-stat customer-stat--teal'>
          <span className='customer-stat__icon'>M</span>
          <span>
            <small>Added this month</small>
            <strong>{totalPerMonth}</strong>
          </span>
        </div>
        <div className='customer-stat customer-stat--blue'>
          <span className='customer-stat__icon'>Y</span>
          <span>
            <small>Added this year</small>
            <strong>{totalPerYear}</strong>
          </span>
        </div>
      </section>
      <section
        className='customer-table-card'
        aria-labelledby='customer-list-title'
      >
        <div className='customer-table-card__header'>
          <div>
            <h2 id='customer-list-title'>Customer list</h2>
            <p>{filteredCustomers.length} customers on this page</p>
          </div>
          <button
            className='customer-export'
            type='button'
            onClick={exportCustomers}
            disabled={isExporting}
          >
            <span aria-hidden='true'>down</span>{' '}
            {isExporting ? 'Preparing export...' : 'Export all customers'}
          </button>
        </div>
        <div className='customer-toolbar'>
          <label className='customer-search'>
            <span aria-hidden='true'>search</span>
            <input
              value={search}
              onChange={(event) => {
                setSearch(event.target.value)
              }}
              placeholder='Search by name, contact name, region, or sales exec.'
              aria-label='Search customers'
            />
          </label>
          <select
            className='customer-filter'
            value={typeFilter}
            onChange={(event) => {
              setTypeFilter(event.target.value)
            }}
            aria-label='Filter by customer type'
          >
            <option value='All'>All</option>
            {customerTypes.map((option) => (
              <option
                key={getOptionValue(option)}
                value={getOptionValue(option)}
              >
                {getOptionLabel(option)}
              </option>
            ))}
          </select>
        </div>
        <div className='customer-table-wrap'>
          <table className='customer-table'>
            <thead>
              <tr>
                <th>Name</th>
                <th>Contact name</th>
                <th>Contact</th>
                <th>Region</th>
                <th>Sales exec</th>
                <th>Type</th>
                <th>
                  <span className='sr-only'>Actions</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td className='customer-table__empty' colSpan='7'>
                    Loading customers...
                  </td>
                </tr>
              ) : visibleCustomers.length ? (
                visibleCustomers.map((customer) => (
                  <tr key={customer.customerid}>
                    <td>
                      <Link
                        className='customer-name-link'
                        to={`/customers/${customer.customerid}`}
                      >
                        {customer.cust_name}
                      </Link>
                      <small>
                        Added{' '}
                        {new Date(
                          customer.customer_date_added,
                        ).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </small>
                    </td>
                    <td>{getContactName(customer)}</td>
                    <td>{customer.cust_contact}</td>
                    <td>{getRegion(customer)}</td>
                    <td>{getSalesExec(customer)}</td>
                    <td>
                      <span className='customer-type'>
                        {getCustomerType(customer)}
                      </span>
                    </td>
                    <td>
                      <div className='customer-actions'>
                        {canEditCustomer && (
                          <button
                            type='button'
                            onClick={() => openEditModal(customer)}
                            disabled={
                              loadingCustomerId ===
                              (customer.id ?? customer.customerid)
                            }
                          >
                            {loadingCustomerId ===
                            (customer.id ?? customer.customerid)
                              ? 'Loading...'
                              : 'Edit'}
                          </button>
                        )}
                        {canDeleteCustomer && (
                          <button
                            className='customer-actions__delete'
                            type='button'
                            onClick={() =>
                              setModal({ mode: 'delete', customer })
                            }
                          >
                            Delete
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td className='customer-table__empty' colSpan='7'>
                    No customers match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div className='customer-pagination'>
          <span>Showing up to 20 customers</span>
          <div>
            <button
              type='button'
              onClick={goToPreviousCursor}
              disabled={!cursorHistory.length || isLoading}
            >
              Previous
            </button>
            <button
              type='button'
              onClick={goToNextCursor}
              disabled={(!nextCursor && !nextPage) || isLoading}
            >
              Next
            </button>
          </div>
        </div>
      </section>
      {modal && (
        <CustomerModal
          mode={modal.mode}
          customer={modal.customer}
          onClose={() => setModal(null)}
          onSave={saveCustomer}
          isSaving={isSaving}
          regions={regions}
          salesExecutives={salesExecutives}
          customerTypes={customerTypes}
        />
      )}
    </div>
  )
}

export default Customer
